import {ThemeIconKey} from 'platform/foundation';

import {PathRouteProps, RouteProps} from 'react-router-dom';

import {Permission} from '../permissions/permissions';

type FeatureFlagCheckFunction = (featureFlag: string) => boolean;
type PathCallback = (isFeatureEnabled: FeatureFlagCheckFunction) => string;

type SingleModuleItemRouterProps = Omit<PathRouteProps, 'children' | 'path'> & {
  path: string | PathCallback;
};

type MultipleModuleItemRouterProps = Omit<PathRouteProps, 'children' | 'path'> & {
  paths: string[];
};

export type ResolveFeatureFlagCallback = (resolve: (flag: string) => boolean) => boolean;

/**
 * Tenant-level gates resolved from async data (e.g. tenant integrations or
 * authorization profiles). Unlike permissions and feature flags, these are NOT known
 * at module-registration time — the item (and its route) is always registered, and the
 * menu entry is filtered reactively at render time once the gating data resolves.
 * The page behind the route must guard itself against the same data source.
 */
export type MenuDynamicAccessKey =
  | 'hyundaiDbiReporting'
  | 'mercedesMraReporting'
  | 'merc5StarReporting'
  | 'mercCstarReporting'
  | 'hyundaiMobisIrisReporting'
  | 'hondaMonthlyReportReporting';

export type Module = {
  id: string;
  routerProps: SingleModuleItemRouterProps;
  layoutProps?: LayoutProps;
  content?: ModuleItem[];
  /**
   * Needs all permissions to pass. This acts as a logical AND
   */
  requiredPermission?: Permission | Permission[];
  /**
   * Needs at least one permission to pass. This acts as a logical OR. If none passes, user is denied access.
   */
  requiredPermissionAtLeastOne?: Permission[];
  requiredFeatureFlag?: string | ResolveFeatureFlagCallback;
  alwaysShowContent?: boolean;
};

export type ModuleItem = SingleModuleItem | MultipleModuleItem;

export type SingleModuleItem = {
  id: string;
  routerProps: SingleModuleItemRouterProps;
  layoutProps?: LayoutProps;
  parentId?: string;
  dynamicAccess?: MenuDynamicAccessKey;
  /**
   * Needs all permissions to pass. This acts as a logical AND
   */
  requiredPermission?: Permission | Permission[];
  /**
   * Needs at least one permission to pass. This acts as a logical OR. If none passes, user is denied access.
   */
  requiredPermissionAtLeastOne?: Permission[];
  requiredFeatureFlag?: string | ResolveFeatureFlagCallback;
  alwaysShow?: boolean;
};

export type MultipleModuleItem = {
  id: string;
  routerProps: MultipleModuleItemRouterProps;
  requiredFeatureFlag?: string;
  layoutProps?: never;
  parentId?: string;
  dynamicAccess?: MenuDynamicAccessKey;
  /**
   * Needs all permissions to pass. This acts as a logical AND
   */
  requiredPermission?: Permission;
  /**
   * Needs at least one permission to pass. This acts as a logical OR. If none passes, user is denied access.
   */
  requiredPermissionAtLeastOne?: Permission[];
  alwaysShow?: boolean;
};

export type LayoutProps = {
  title: string;
  isLastMenuItem?: boolean;
} & (
  | {
      icon: ThemeIconKey;
      isPartOfCreateMenuItem?: never;
      position?: never;
    }
  | {
      icon?: never;
      isPartOfCreateMenuItem: boolean;
      position?: number;
    }
);

export type MenuItemProps = {
  id: string;
  path: string;
  layoutProps: LayoutProps;
  parentId?: string;
  content?: MenuItemProps[];
  alwaysShowContent?: boolean;
  action?: () => void | Promise<void>;
  dynamicAccess?: MenuDynamicAccessKey;
};

export type Register = {
  menuItems: MenuItemProps[];
  createMenuItems: MenuItemProps[];
  routes: RouteProps[];
  reachableRoutePaths: string[];
};
